import { Link, router } from '@inertiajs/react'
import { useState, useMemo } from 'react'
import { Layout } from '@/components/Layout'
import { PageHead } from '@/components/PageHead'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Activity, CheckCircle, XCircle, Clock, AlertTriangle, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { DateRangePicker } from '@/components/DateRangePicker'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'

interface Metrics {
  total_executions: number
  success_rate: number
  total_failures: number
  time_saved_minutes: number
  time_saved_hours: number
}

interface ClientSummary {
  id: number
  name: string
  code: string
  instances_count: number
  total_executions: number
  failures: number
  success_rate: number
  time_saved_minutes: number
  time_saved_hours: number
}

interface FailingWorkflow {
  id: number
  name: string
  client_name: string
  instance_name: string
  failure_count: number
  total_executions: number
  success_rate: number
}

interface ClientAtRisk {
  id: number
  name: string
  total_executions: number
  failures: number
  success_rate: number
}

interface DateRange {
  start_date: string
  end_date: string
}

interface ExecutionChartData {
  date: string
  total: number
  successful: number
  failed: number
}

interface Props {
  metrics: Metrics
  executions_chart: ExecutionChartData[]
  clients_summary: ClientSummary[]
  top_failing_workflows: FailingWorkflow[]
  clients_at_risk: ClientAtRisk[]
  date_range: DateRange
}

type ClientSortKey = 'name' | 'instances_count' | 'total_executions' | 'failures' | 'success_rate' | 'time_saved_minutes'
type WorkflowSortKey = 'name' | 'failure_count' | 'success_rate'
type RiskSortKey = 'name' | 'failures' | 'success_rate'
type SortDirection = 'asc' | 'desc'

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toString()
}

function formatTimeSaved(hours: number, minutes: number): string {
  if (hours >= 1) return `${hours}h`
  return `${minutes}m`
}

export default function OverviewIndex({ metrics, executions_chart, clients_summary, top_failing_workflows, clients_at_risk, date_range }: Props) {
  const hasData = metrics.total_executions > 0
  const hasChartData = executions_chart.some(d => d.total > 0)

  // Clients table sorting
  const [clientSortKey, setClientSortKey] = useState<ClientSortKey | null>('total_executions')
  const [clientSortDirection, setClientSortDirection] = useState<SortDirection>('desc')

  // Workflows table sorting
  const [workflowSortKey, setWorkflowSortKey] = useState<WorkflowSortKey | null>('failure_count')
  const [workflowSortDirection, setWorkflowSortDirection] = useState<SortDirection>('desc')

  // Risk table sorting
  const [riskSortKey, setRiskSortKey] = useState<RiskSortKey | null>('failures')
  const [riskSortDirection, setRiskSortDirection] = useState<SortDirection>('desc')

  // Sort clients
  const sortedClients = useMemo(() => {
    if (!clientSortKey) return clients_summary
    return [...clients_summary].sort((a, b) => {
      const aVal = a[clientSortKey]
      const bVal = b[clientSortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return clientSortDirection === 'asc' ? 1 : -1
      if (bVal == null) return clientSortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return clientSortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return clientSortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [clients_summary, clientSortKey, clientSortDirection])

  // Sort workflows
  const sortedWorkflows = useMemo(() => {
    if (!workflowSortKey) return top_failing_workflows
    return [...top_failing_workflows].sort((a, b) => {
      const aVal = a[workflowSortKey]
      const bVal = b[workflowSortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return workflowSortDirection === 'asc' ? 1 : -1
      if (bVal == null) return workflowSortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return workflowSortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return workflowSortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [top_failing_workflows, workflowSortKey, workflowSortDirection])

  // Sort clients at risk
  const sortedRiskClients = useMemo(() => {
    if (!riskSortKey) return clients_at_risk
    return [...clients_at_risk].sort((a, b) => {
      const aVal = a[riskSortKey]
      const bVal = b[riskSortKey]
      if (aVal == null && bVal == null) return 0
      if (aVal == null) return riskSortDirection === 'asc' ? 1 : -1
      if (bVal == null) return riskSortDirection === 'asc' ? -1 : 1
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return riskSortDirection === 'asc' ? aVal - bVal : bVal - aVal
      }
      return riskSortDirection === 'asc'
        ? String(aVal).localeCompare(String(bVal))
        : String(bVal).localeCompare(String(aVal))
    })
  }, [clients_at_risk, riskSortKey, riskSortDirection])

  const handleClientSort = (key: ClientSortKey) => {
    if (clientSortKey === key) {
      setClientSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setClientSortKey(key)
      setClientSortDirection('asc')
    }
  }

  const handleWorkflowSort = (key: WorkflowSortKey) => {
    if (workflowSortKey === key) {
      setWorkflowSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setWorkflowSortKey(key)
      setWorkflowSortDirection('asc')
    }
  }

  const handleRiskSort = (key: RiskSortKey) => {
    if (riskSortKey === key) {
      setRiskSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setRiskSortKey(key)
      setRiskSortDirection('asc')
    }
  }

  const ClientSortIcon = ({ column }: { column: ClientSortKey }) => {
    if (clientSortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return clientSortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const WorkflowSortIcon = ({ column }: { column: WorkflowSortKey }) => {
    if (workflowSortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return workflowSortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const RiskSortIcon = ({ column }: { column: RiskSortKey }) => {
    if (riskSortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />
    return riskSortDirection === 'asc' ? <ArrowUp className="ml-1 h-3 w-3" /> : <ArrowDown className="ml-1 h-3 w-3" />
  }

  const handleDateRangeChange = (range: { from: Date; to: Date }) => {
    router.get('/', {
      start_date: range.from.toISOString().split('T')[0],
      end_date: range.to.toISOString().split('T')[0],
    }, {
      preserveState: true,
      preserveScroll: true,
    })
  }

  const dateValue = {
    from: new Date(date_range.start_date),
    to: new Date(date_range.end_date),
  }

  return (
    <Layout>
      <PageHead title="Overview" description="Dashboard showing execution metrics, success rates, and time saved across all n8n instances." />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Overview</h1>
            <p className="text-muted-foreground">
              Dashboard showing metrics for the selected period.
            </p>
          </div>
          <DateRangePicker value={dateValue} onChange={handleDateRangeChange} />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Executions</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {hasData ? formatNumber(metrics.total_executions) : '-'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Success Rate</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {hasData ? `${metrics.success_rate}%` : '-'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Failures</CardTitle>
              <XCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">
                {hasData ? formatNumber(metrics.total_failures) : '-'}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Time Saved</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {hasData ? `${metrics.time_saved_hours}h` : '-'}
              </div>
              {hasData && metrics.time_saved_minutes > 0 && (
                <p className="text-xs text-muted-foreground">
                  {metrics.time_saved_minutes} minutes
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Executions Chart */}
        <Card>
          <CardHeader>
            <CardTitle>Executions Over Time</CardTitle>
            <CardDescription>
              Daily execution counts for the selected period
            </CardDescription>
          </CardHeader>
          <CardContent>
            {hasChartData ? (
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={executions_chart}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="date"
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <YAxis
                      className="text-xs"
                      tick={{ fill: 'hsl(var(--muted-foreground))' }}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--background))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '6px',
                      }}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="successful"
                      name="Successful"
                      stroke="hsl(142, 76%, 36%)"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="failed"
                      name="Failed"
                      stroke="hsl(var(--destructive))"
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="flex h-[300px] flex-col items-center justify-center text-muted-foreground">
                <Activity className="h-8 w-8 mb-2" />
                <p>No execution data for this period</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Clients Summary Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Top Clients by Activity</CardTitle>
                <CardDescription>
                  Top 10 clients by executions for the selected period.
                </CardDescription>
              </div>
              <Link
                href="/clients"
                className="text-sm text-primary hover:underline"
              >
                View all clients →
              </Link>
            </div>
          </CardHeader>
          <CardContent>
            {clients_summary.length === 0 ? (
              <div className="flex flex-col items-center py-8 text-muted-foreground">
                <Activity className="h-8 w-8 mb-2" />
                <p>No clients found. Add a client to get started.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button onClick={() => handleClientSort('name')} className="flex items-center font-medium hover:text-foreground">
                        Client <ClientSortIcon column="name" />
                      </button>
                    </TableHead>
                    <TableHead className="text-center">
                      <button onClick={() => handleClientSort('instances_count')} className="flex items-center justify-center font-medium hover:text-foreground w-full">
                        Instances <ClientSortIcon column="instances_count" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleClientSort('total_executions')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Executions <ClientSortIcon column="total_executions" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleClientSort('failures')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Failures <ClientSortIcon column="failures" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleClientSort('success_rate')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Success Rate <ClientSortIcon column="success_rate" />
                      </button>
                    </TableHead>
                    <TableHead className="text-right">
                      <button onClick={() => handleClientSort('time_saved_minutes')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                        Time Saved <ClientSortIcon column="time_saved_minutes" />
                      </button>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedClients.map((client) => (
                    <TableRow key={client.id}>
                      <TableCell>
                        <Link
                          href={`/clients/${client.id}`}
                          className="font-medium hover:underline"
                        >
                          {client.name}
                        </Link>
                        <p className="text-xs text-muted-foreground">
                          {client.code}
                        </p>
                      </TableCell>
                      <TableCell className="text-center">
                        {client.instances_count}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(client.total_executions)}
                      </TableCell>
                      <TableCell className="text-right">
                        {client.failures > 0 ? (
                          <Badge variant="destructive">{client.failures}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={client.success_rate < 80 ? 'text-destructive font-medium' : ''}>
                          {client.total_executions > 0 ? `${client.success_rate}%` : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {client.time_saved_hours > 0 || client.time_saved_minutes > 0
                          ? formatTimeSaved(client.time_saved_hours, client.time_saved_minutes)
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <div className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>Top Failing Workflows</CardTitle>
                <CardDescription>
                  Workflows with the most failures in the selected period.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {top_failing_workflows.length === 0 ? (
                  <div className="flex flex-col items-center py-8 text-muted-foreground">
                    <CheckCircle className="h-8 w-8 mb-2" />
                    <p>No failing workflows!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <button onClick={() => handleWorkflowSort('name')} className="flex items-center font-medium hover:text-foreground">
                            Workflow <WorkflowSortIcon column="name" />
                          </button>
                        </TableHead>
                        <TableHead className="text-right">
                          <button onClick={() => handleWorkflowSort('failure_count')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                            Failures <WorkflowSortIcon column="failure_count" />
                          </button>
                        </TableHead>
                        <TableHead className="text-right">
                          <button onClick={() => handleWorkflowSort('success_rate')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                            Success Rate <WorkflowSortIcon column="success_rate" />
                          </button>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedWorkflows.map((workflow) => (
                        <TableRow key={workflow.id}>
                          <TableCell>
                            <Link
                              href={`/workflows/${workflow.id}`}
                              className="font-medium hover:underline"
                            >
                              {workflow.name}
                            </Link>
                            <p className="text-xs text-muted-foreground">
                              {workflow.client_name} / {workflow.instance_name}
                            </p>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="destructive">{workflow.failure_count}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={workflow.success_rate < 80 ? 'text-destructive' : ''}>
                              {workflow.success_rate}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Clients at Risk
                </CardTitle>
                <CardDescription>
                  Clients with high failure rates or many failures.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {clients_at_risk.length === 0 ? (
                  <div className="flex flex-col items-center py-8 text-muted-foreground">
                    <CheckCircle className="h-8 w-8 mb-2" />
                    <p>All clients are healthy!</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          <button onClick={() => handleRiskSort('name')} className="flex items-center font-medium hover:text-foreground">
                            Client <RiskSortIcon column="name" />
                          </button>
                        </TableHead>
                        <TableHead className="text-right">
                          <button onClick={() => handleRiskSort('failures')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                            Failures <RiskSortIcon column="failures" />
                          </button>
                        </TableHead>
                        <TableHead className="text-right">
                          <button onClick={() => handleRiskSort('success_rate')} className="flex items-center justify-end font-medium hover:text-foreground w-full">
                            Success Rate <RiskSortIcon column="success_rate" />
                          </button>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedRiskClients.map((client) => (
                        <TableRow key={client.id}>
                          <TableCell>
                            <Link
                              href={`/clients/${client.id}`}
                              className="font-medium hover:underline"
                            >
                              {client.name}
                            </Link>
                            <p className="text-xs text-muted-foreground">
                              {client.total_executions} executions
                            </p>
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="destructive">{client.failures}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={client.success_rate < 80 ? 'text-destructive' : ''}>
                              {client.success_rate}%
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
      </div>
    </Layout>
  )
}
